// Smart notification templates for the admin Notifications page. Some are
// evergreen (always available); others are generated from live dashboard data
// (the next service plan, the roster) so the page is useful, not empty.
import type { LucideIcon } from 'lucide-react';
import {
  CalendarClock,
  CalendarHeart,
  ListChecks,
  UserPlus,
  CalendarCheck,
  Music4,
  Megaphone,
} from 'lucide-react';
import type { ServicePlan, EnrichedMembership } from '@app/types';

export type TemplateAudience = 'church' | 'members';

export interface NotificationTemplate {
  id: string;
  /** Grouping label shown as a small caption. */
  category: string;
  /** Card heading. */
  cardTitle: string;
  /** One-line description of when to use it. */
  blurb: string;
  icon: LucideIcon;
  /** CSS gradient for the icon chip (brand palette only). */
  accent: string;
  audience: TemplateAudience;
  /** Prefilled push title + body shown in the mini preview. */
  draft: { title: string; body: string; url?: string };
  /** Preselected member ids for a member-targeted template. */
  memberIds?: string[];
  /** Human summary of the target (e.g. "6 on the schedule"). */
  audienceSummary: string;
  /** Whether this was generated from live data or is evergreen. */
  source: 'auto' | 'evergreen';
}

export interface TemplateContext {
  upcoming: ServicePlan | null;
  roster: EnrichedMembership[];
  churchName?: string;
}

const ACCENT = {
  tealPink: 'linear-gradient(135deg, #56b6da 0%, #FC5767 100%)',
  pink: 'linear-gradient(135deg, #FC5767 0%, #E4ABBF 100%)',
  lavender: 'linear-gradient(135deg, #E4ABBF 0%, #FC5767 100%)',
  teal: 'linear-gradient(135deg, #56b6da 0%, #7fd0e8 100%)',
};

function formatServiceDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'this week';
  return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'short', day: 'numeric' }).format(d);
}

function isThisWeek(iso: string): boolean {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  const diff = (d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
  return diff >= -1 && diff <= 7;
}

/** People serving at the next plan (by user id). */
function serviceTeamIds(upcoming: ServicePlan | null): string[] {
  if (!upcoming?.team) return [];
  return Array.from(new Set(upcoming.team.map((t) => t.userId).filter(Boolean)));
}

/** Roster members whose role/function reads like a leader. */
function leaderIds(roster: EnrichedMembership[]): string[] {
  const leaderRe = /lead/i;
  return Array.from(
    new Set(
      roster
        .filter((m) => m.isActive && (leaderRe.test(m.function ?? '') || leaderRe.test(m.role ?? '')))
        .map((m) => m.userId),
    ),
  );
}

function memberSummary(count: number, noun: string): string {
  if (count === 0) return `Pick your ${noun}`;
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/**
 * Build the template list from live context. Auto templates come first when
 * data is available; evergreen templates always follow so the page is populated.
 */
export function buildTemplates(ctx: TemplateContext): NotificationTemplate[] {
  const templates: NotificationTemplate[] = [];
  const { upcoming, roster } = ctx;
  const activeRoster = roster.filter((m) => m.isActive);

  // ---- Auto: derived from the next service plan ----
  if (upcoming && isThisWeek(upcoming.date)) {
    const day = formatServiceDay(upcoming.date);
    const serviceName = upcoming.title?.trim() || 'our service';
    const songCount = upcoming.items?.filter((i) => i.type === 'Song').length ?? 0;

    templates.push({
      id: 'auto-service-reminder',
      category: 'This week',
      cardTitle: 'Service reminder',
      blurb: `Remind everyone that ${serviceName} is coming up on ${day}.`,
      icon: CalendarHeart,
      accent: ACCENT.tealPink,
      audience: 'church',
      draft: {
        title: `See you ${day.split(',')[0]}`,
        body: `${serviceName} is ${day}. We can't wait to worship together${songCount ? `. ${songCount} songs planned` : ''}.`,
      },
      audienceSummary: 'Everyone in your church',
      source: 'auto',
    });

    const teamIds = serviceTeamIds(upcoming);
    templates.push({
      id: 'auto-confirm-availability',
      category: 'This week',
      cardTitle: 'Confirm availability',
      blurb: `Ask the people serving at ${serviceName} to confirm they can make it.`,
      icon: CalendarCheck,
      accent: ACCENT.pink,
      audience: 'members',
      draft: {
        title: 'Can you serve this week?',
        body: `Please confirm your availability for ${serviceName} on ${day}.`,
      },
      memberIds: teamIds,
      audienceSummary: memberSummary(teamIds.length, 'on the schedule'),
      source: 'auto',
    });

    if (upcoming.isSetlistPublished === false || upcoming.isPublished === false) {
      const leaders = leaderIds(activeRoster);
      const targetIds = leaders.length ? leaders : teamIds;
      templates.push({
        id: 'auto-setlist-pending',
        category: 'This week',
        cardTitle: 'Set list not published',
        blurb: `The set list for ${serviceName} isn't published yet. Nudge the worship leaders.`,
        icon: ListChecks,
        accent: ACCENT.lavender,
        audience: 'members',
        draft: {
          title: 'Set list needs publishing',
          body: `The set list for ${serviceName} on ${day} isn't published yet. Please finalize it so the team can prepare.`,
        },
        memberIds: targetIds,
        audienceSummary: memberSummary(targetIds.length, 'worship leader'),
        source: 'auto',
      });
    }
  }

  // ---- Evergreen: always available ----
  const leaders = leaderIds(activeRoster);
  const teamIds = serviceTeamIds(upcoming);
  const rehearsalTargets = leaders.length ? leaders : teamIds;

  templates.push({
    id: 'evergreen-rehearsal',
    category: 'For the team',
    cardTitle: 'Rehearsal reminder',
    blurb: 'Let your service leaders and team know when to arrive for rehearsal.',
    icon: Music4,
    accent: ACCENT.teal,
    audience: 'members',
    draft: {
      title: 'Rehearsal reminder',
      body: 'Rehearsal is coming up. Please arrive 30 minutes early so we can run through the set.',
    },
    memberIds: rehearsalTargets,
    audienceSummary: memberSummary(rehearsalTargets.length, 'team member'),
    source: 'evergreen',
  });

  templates.push({
    id: 'evergreen-prepare-setlist',
    category: 'For the team',
    cardTitle: 'Prepare the set list',
    blurb: 'Ask your worship leaders to build and publish this week\'s set list.',
    icon: ListChecks,
    accent: ACCENT.lavender,
    audience: 'members',
    draft: {
      title: 'Set list for this week',
      body: 'Please prepare and publish the set list for this week so the team can start practicing.',
    },
    memberIds: rehearsalTargets,
    audienceSummary: memberSummary(rehearsalTargets.length, 'worship leader'),
    source: 'evergreen',
  });

  templates.push({
    id: 'evergreen-availability',
    category: 'For your church',
    cardTitle: 'Set your availability',
    blurb: 'Encourage members to open the app and mark the days they can serve.',
    icon: CalendarClock,
    accent: ACCENT.pink,
    audience: 'church',
    draft: {
      title: 'Set your availability',
      body: 'Open the app and tell your team the days you are available to serve this month.',
    },
    audienceSummary: 'Everyone in your church',
    source: 'evergreen',
  });

  templates.push({
    id: 'evergreen-invite',
    category: 'For your church',
    cardTitle: 'Weekend invite',
    blurb: 'A warm invitation to join this weekend and bring a friend.',
    icon: Megaphone,
    accent: ACCENT.tealPink,
    audience: 'church',
    draft: {
      title: 'Join us this weekend',
      body: 'We would love to see you this weekend. Come as you are, and bring a friend!',
    },
    audienceSummary: 'Everyone in your church',
    source: 'evergreen',
  });

  templates.push({
    id: 'evergreen-welcome',
    category: 'For your church',
    cardTitle: 'Welcome new members',
    blurb: 'Greet everyone who recently joined and point them to next steps.',
    icon: UserPlus,
    accent: ACCENT.lavender,
    audience: 'church',
    draft: {
      title: 'Welcome to the family',
      body: 'So glad you are here! Check the app for upcoming services and ways to get involved.',
    },
    audienceSummary: 'Everyone in your church',
    source: 'evergreen',
  });

  return templates;
}
